import { Dropdown } from '@sqs/rosetta-compositions';
import { Button, Flex, Text } from '@sqs/rosetta-primitives';
import { useTheme } from '@sqs/rosetta-styled';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { addUserToSchedulingInstance } from '../../apis/schedulingInstances';
import { usePlatformBreakpoint } from '../../hooks/usePlatformBreakpoint';
import { useI18n } from '../../i18n';
import { SchedulingInstancePermission, SchedulingInstancePermissionData } from '../../models/SchedulingInstance';
import { selectEnterprise } from '../../stores/currentUser';
import { sendErrorMessage, sendSuccessMessage } from '../../stores/messages';
import { RootState } from '../../stores/rootReducer';
import { fetchSchedulingInstances, selectAll } from '../../stores/schedulingInstances';
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { GenericModal } from '../common/GenericModal';
import { ModalTitle } from '../common/ModalTitle';
import { AddUserToInstanceForm } from '../common/types/UserInstanceForm';
import { SetUserPermissions } from '../common/UserPermissions/SetUserPermissions';

interface UserAddToSchedulingInstanceModalProps {
  readonly orgUserId: number;
  readonly closeModal: () => void;
  readonly refresh?: () => void;
}

export const UserAddToSchedulingInstanceModal = ({
  orgUserId,
  closeModal,
  refresh
}: UserAddToSchedulingInstanceModalProps) => {
  const { T, t } = useI18n();
  const { colors } = useTheme();
  const dispatch = useAppDispatch();
  const { isMobile } = usePlatformBreakpoint();

  const { enterpriseId, schedulingInstances, hasLoaded } = useAppSelector((state: RootState) => ({
    enterpriseId: selectEnterprise(state.currentUser)?.id,
    schedulingInstances: selectAll(state.schedulingInstances),
    hasLoaded: !state.schedulingInstances.isLoading
  }));

  const formMethods = useForm<AddUserToInstanceForm>({
    defaultValues: {
      schedulingInstanceId: 0,
      permission: '',
      allowExports: false,
      allItems: false,
      items: [],
      totalItems: 0,
      checkedItemIds: []
    }
  });

  useEffect(() => {
    if (enterpriseId && !hasLoaded) {
      dispatch(fetchSchedulingInstances(enterpriseId));
    }
  }, []);

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch
  } = formMethods;

  // Because Rosetta's built-in onChange function doesn't play well with react-hook-form, and we need these values
  //   for other checks, we watch them instead of using the expanded `register` function
  const selectedInstance = watch('schedulingInstanceId');

  register('schedulingInstanceId', {
    validate: {
      positive: (v) => v > 0 || t("Please choose a scheduling instance", {}, { project: 'enterprise-dashboard' })
    }
  });
  register('permission', { required: t("Please choose permission level", {}, { project: 'enterprise-dashboard' }) });

  const triggerCloseModal = () => {
    closeModal();
    reset();
  };

  const submitUserToInstance = handleSubmit((formData) => {
    const instance = schedulingInstances.find((i) => i.id === formData.schedulingInstanceId);

    if (instance) {
      const calendars = !formData.allItems ? formData.checkedItemIds : [];

      const data: SchedulingInstancePermissionData = {
        permissions: formData.permission as SchedulingInstancePermission,
        allowExports: Boolean(formData.allowExports),
        calendars: calendars || [],
        allCalendars: formData.allItems
      };
      if (enterpriseId) {
        addUserToSchedulingInstance(enterpriseId, instance.id, orgUserId, data).
        then(() => {
          dispatch(
            sendSuccessMessage(t("User was added to {instance}",


            { instance: instance?.instanceName }, {
              project: 'enterprise-dashboard' })

            )
          );
          dispatch(fetchSchedulingInstances(enterpriseId));
          triggerCloseModal();
          if (refresh) {
            refresh();
          }
        }).
        catch(() => {
          dispatch(
            sendErrorMessage(t("There was a problem adding the user to the scheduling instance",
            null, {
              project: 'enterprise-dashboard' })

            )
          );
        });
      }
    } else {
      dispatch(sendErrorMessage(t("Unable to find scheduling instance", null, { project: 'enterprise-dashboard' })));
    }
  });

  return (
    <GenericModal
      closeModal={triggerCloseModal}
      width="404px"
      xPadding="48px"
      modalActions={
      <Flex justifyContent="space-between" mt={isMobile ? 0 : 6}>
          <Button.Tertiary type="button" onClick={triggerCloseModal} size="medium">
            <T project="enterprise-dashboard">{"Cancel"}</T>
          </Button.Tertiary>
          <Button.Primary type="submit" size="medium" onClick={submitUserToInstance}>
            <T project="enterprise-dashboard">{"Save"}</T>
          </Button.Primary>
        </Flex>
      }>

      <FormProvider {...formMethods}>
        <ModalTitle>
          <T project="enterprise-dashboard">{"Add user to a Scheduling Instance"}</T>
        </ModalTitle>
        <Text.Label>
          <T project="enterprise-dashboard">{"Scheduling Instance"}</T>
        </Text.Label>
        <Dropdown
          placement="bottom"
          onChange={(v: number) => setValue('schedulingInstanceId', v, { shouldDirty: true })}
          value={selectedInstance}
          renderDropdown={(props: any) => <Dropdown.Dropdown {...props} sx={{ background: colors.white }} />}>

          {schedulingInstances?.map((instance) =>
          <Dropdown.Option key={instance.id} value={instance.id}>
              {instance.instanceName}
            </Dropdown.Option>
          )}
        </Dropdown>
        {errors.schedulingInstanceId &&
        <Text.Caption sx={{ color: colors.red[400] }} role="alert">
            {errors.schedulingInstanceId.message}
          </Text.Caption>
        }
        <SetUserPermissions showVariantSelector />
      </FormProvider>
    </GenericModal>);

};